import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { gurukulService, viewCounterService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import { formatCurrency, formatDate } from '../../utils/formatters';
import PartyDonationModal from '../../components/common/PartyDonationModal';
import QRCode from 'react-qr-code';
import Logo from '../../components/common/Logo';

/** Site origin for QR / share links. Uses env in production builds; otherwise current tab origin (LAN IP works for phone scans). */
const getPublicSiteOrigin = () => {
  const raw = process.env.REACT_APP_PUBLIC_SITE_URL;
  if (raw && String(raw).trim()) {
    try {
      const u = new URL(raw.includes('://') ? raw : `https://${raw}`);
      return u.origin;
    } catch {
      return String(raw).replace(/\/$/, '');
    }
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://fundraiser.gurukulfundraiser.org';
};

// Simple brand-specific social icons (normalized size)
const SocialIcon = ({ platform }) => {
  const key = (platform || '').toLowerCase();

  if (key === 'facebook') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22 12.07C22 6.48 17.52 2 11.93 2 6.35 2 1.87 6.48 1.87 12.07c0 4.83 3.44 8.84 7.94 9.78v-6.92H7.4V12.1h2.41v-2.2c0-2.38 1.42-3.7 3.6-3.7 1.04 0 2.13.19 2.13.19v2.35h-1.2c-1.18 0-1.55.73-1.55 1.48v1.89h2.64l-.42 2.83h-2.22v6.92c4.5-.94 7.94-4.95 7.94-9.78z" />
      </svg>
    );
  }

  if (key === 'twitter' || key === 'x') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.25 2H21L14.25 10.7 22 22h-5.5L11.9 14.4 6.5 22H4L11.25 12.7 4 2h5.6l4.22 6.9L18.25 2z" />
      </svg>
    );
  }

  if (key === 'instagram') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 2C4.243 2 2 4.243 2 7v10c0 2.757 2.243 5 5 5h10c2.757 0 5-2.243 5-5V7c0-2.757-2.243-5-5-5H7zm0 2h10c1.654 0 3 1.346 3 3v10c0 1.654-1.346 3-3 3H7c-1.654 0-3-1.346-3-3V7c0-1.654 1.346-3 3-3zm5 2.5A5.51 5.51 0 006.5 12 5.51 5.51 0 0012 17.5 5.51 5.51 0 0017.5 12 5.51 5.51 0 0012 6.5zm0 2A3.51 3.51 0 0115.5 12 3.51 3.51 0 0112 15.5 3.51 3.51 0 018.5 12 3.51 3.51 0 0112 8.5zM18 6a1 1 0 110 2 1 1 0 010-2z" />
      </svg>
    );
  }

  if (key === 'linkedin') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M4.98 3.5C4.98 4.88 3.88 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1 4.98 2.12 4.98 3.5zM.5 8h4V24h-4V8zm7.5 0h3.84v2.18h.05c.53-1 1.82-2.18 3.74-2.18 4 0 4.74 2.63 4.74 6.05V24h-4v-7.25c0-1.73-.03-3.95-2.4-3.95-2.4 0-2.77 1.87-2.77 3.8V24h-4V8z" />
      </svg>
    );
  }

  if (key === 'youtube') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21.8 8.001a2.753 2.753 0 00-1.94-1.947C18.25 5.5 12 5.5 12 5.5s-6.25 0-7.86.554A2.753 2.753 0 002.2 8.001 28.44 28.44 0 001.7 12a28.44 28.44 0 00.5 3.999 2.753 2.753 0 001.94 1.947C5.75 18.5 12 18.5 12 18.5s6.25 0 7.86-.554a2.753 2.753 0 001.94-1.947A28.44 28.44 0 0022.3 12a28.44 28.44 0 00-.5-3.999zM10 15.2V8.8L15.2 12 10 15.2z" />
      </svg>
    );
  }

  if (key === 'tiktok') {
    return (
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.5 7.5c-1.3-.4-2.4-1.3-3-2.5-.3-.6-.5-1.3-.6-2H11v11.6c0 1-.8 1.9-1.9 1.9S7.2 15.6 7.2 14.5 8.1 12.6 9.2 12.6c.3 0 .6.1.9.2V9.3c-.3-.1-.6-.1-.9-.1-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5V9c.9.8 2.1 1.4 3.3 1.5v-3z" />
      </svg>
    );
  }

  // website / default - simple globe icon
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <circle cx="12" cy="12" r="9" strokeWidth="2" />
      <path d="M3 12h18" strokeWidth="2" />
      <path d="M12 3c-2.5 3-3.5 6-3.5 9s1 6 3.5 9c2.5-3 3.5-6 3.5-9s-1-6-3.5-9z" strokeWidth="2" />
    </svg>
  );
};

const GurukulProfile = () => {
  const { linkOrId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bankCopiedKey, setBankCopiedKey] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const qrRef = useRef(null);

  useEffect(() => {
    // Always start from top when opening a Gurukul profile
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    setImageError(false); // Reset image error when candidate changes
    fetchCandidate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkOrId]);

  const viewCountedForCandidate = useRef(null);
  useEffect(() => {
    if (!candidate?.id || viewCountedForCandidate.current === candidate.id) return;
    viewCountedForCandidate.current = candidate.id;
    viewCounterService.incrementGurukul(candidate.id).catch(() => { });
  }, [candidate?.id]);

  // ?is-qr=true — open donation modal (e.g. after scanning profile QR from another device)
  useEffect(() => {
    const isQrScan = searchParams.get('is-qr');
    const shouldOpen =
      candidate &&
      (isQrScan === 'true' || isQrScan === '1' || isQrScan === 'yes');
    if (!shouldOpen) return;

    setShowDonateModal(true);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('is-qr');
        return next;
      },
      { replace: true }
    );
  }, [candidate, searchParams, setSearchParams]);

  const fetchCandidate = async () => {
    try {
      const response = await gurukulService.getGurukulProfile(linkOrId);
      // Handle API response structure: { status, statusCode, data: { candidate details } }
      const candidateData = response?.data || response;
      setCandidate(candidateData);

      // Keep URL stable (use ID everywhere)
    } catch (error) {
      console.error('Error fetching candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDonateClick = () => {
    setShowDonateModal(true);
  };

  const scanToDonateUrl = useMemo(() => {
    if (!candidate?.id) return '';
    return `${getPublicSiteOrigin()}/gurukuls/${candidate.id}?is-qr=true`;
  }, [candidate?.id]);

  const shareUrl = scanToDonateUrl;


  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const copyBankText = async (key, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      setBankCopiedKey(key);
      setTimeout(() => setBankCopiedKey(null), 1500);
    } catch (err) {
      console.error('Failed to copy bank detail:', err);
    }
  };

  const CopyIcon = ({ className = 'w-4 h-4' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h8a2 2 0 012 2v10a2 2 0 01-2 2H8a2 2 0 01-2-2V9a2 2 0 012-2z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 17H5a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v1" />
    </svg>
  );

  const CheckIcon = ({ className = 'w-4 h-4' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );

  const handleShare = async () => {
    if (!shareUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Support ${candidate?.name}`,
          text: `Check out ${candidate?.name}'s profile and support them!`,
          url: shareUrl,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      handleCopyLink();
    }
  };

  const handleDownloadQR = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      const scale = 3; // High resolution multiplier
      const padding = 60 * scale;
      const qrSize = 300 * scale;
      const headerHeight = 100 * scale;
      const footerHeight = 120 * scale;
      const totalWidth = qrSize + (padding * 2);
      const totalHeight = headerHeight + qrSize + footerHeight + (padding * 2);

      canvas.width = totalWidth;
      canvas.height = totalHeight;

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Header text - "Support"
      ctx.fillStyle = '#2563eb';
      ctx.font = `bold ${36 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText('🙏 Support', totalWidth / 2, padding + 40 * scale);

      // Gurukul name
      ctx.fillStyle = '#1f2937';
      ctx.font = `bold ${32 * scale}px Arial`;
      const gurukulName = candidate?.name || 'Gurukul';
      ctx.fillText(gurukulName, totalWidth / 2, padding + 85 * scale);

      // Draw QR code
      ctx.drawImage(img, padding, headerHeight + padding, qrSize, qrSize);

      // Footer - Constituency info
      ctx.fillStyle = '#6b7280';
      ctx.font = `${24 * scale}px Arial`;
      const location = `${candidate?.constituency || ''}, ${candidate?.district || ''} `;
      ctx.fillText(location, totalWidth / 2, headerHeight + padding + qrSize + 40 * scale);

      // Scan to donate text
      ctx.fillStyle = '#2563eb';
      ctx.font = `bold ${26 * scale}px Arial`;
      ctx.fillText('Scan to Donate', totalWidth / 2, headerHeight + padding + qrSize + 80 * scale);

      // Gurukul branding
      ctx.fillStyle = '#9ca3af';
      ctx.font = `${20 * scale}px Arial`;
      ctx.fillText('Gurukul Fundraiser', totalWidth / 2, headerHeight + padding + qrSize + 115 * scale);

      const link = document.createElement('a');
      link.download = `Support_${candidate?.name?.replace(/\s+/g, '_')}_QR.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const quickAmounts = [100, 500, 1000, 5000, 10000];

  const DetailItem = ({ label, value, icon }) => {
    if (!value || (Array.isArray(value) && value.length === 0)) return null;
    const isList = Array.isArray(value);

    return (
      <div className="flex items-start gap-3 p-4 bg-white rounded-xl">
        {icon && !isList && <div className="mt-1 text-primary-600">{icon}</div>}
        <div className="w-full">
          <p className="text-sm md:text-base font-bold text-gray-500 uppercase tracking-wider mb-2">{label}</p>

          {isList ? (
            <div className="flex flex-col gap-2">
              {value.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 text-primary-600">
                    <svg className="w-4 h-4 md:w-5 md:h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm-1.999 14.413-3.713-3.705L7.7 11.292l2.299 2.295 5.294-5.294 1.414 1.414-6.706 6.706z" />
                    </svg>
                  </span>
                  <span className="text-base md:text-lg text-gray-900 font-medium whitespace-pre-wrap">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-base md:text-lg text-gray-900 font-medium whitespace-pre-wrap">
              {value}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Very small markdown helper for the bio field (supports **bold** + paragraphs)
  const renderMarkdownText = (text) => {
    if (!text) return null;

    const paragraphs = String(text).split(/\n{2,}/);

    return paragraphs.map((para, idx) => {
      const parts = [];
      let lastIndex = 0;
      const boldRegex = /\*\*(.+?)\*\*/g;
      let match;

      while ((match = boldRegex.exec(para)) !== null) {
        if (match.index > lastIndex) {
          parts.push(para.slice(lastIndex, match.index));
        }
        parts.push(<strong key={`b-${idx}-${lastIndex}`}>{match[1]}</strong>);
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < para.length) {
        parts.push(para.slice(lastIndex));
      }

      return (
        <p key={idx} className="mb-2 last:mb-0">
          {parts}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Gurukul not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100/80 mb-12">
          {/* Static Cover with Gurukul Branding */}
          <div className="h-56 md:h-72 relative overflow-hidden">
            {/* Pattern Overlay / Custom Background */}
            <div
              className={`absolute inset-0 ${
                candidate.backgroundImageUrl
                  ? 'group cursor-pointer bg-cover bg-center bg-no-repeat'
                  : 'bg-gradient-to-r from-primary-700 via-primary-600 to-primary-800'
              }`}
              style={{
                backgroundImage: candidate.backgroundImageUrl
                  ? `url(${candidate.backgroundImageUrl})`
                  : undefined,
                backgroundSize: candidate.backgroundImageUrl ? 'cover' : undefined
              }}
              onClick={candidate.backgroundImageUrl ? () => setSelectedImage(candidate.backgroundImageUrl) : undefined}
              title={candidate.backgroundImageUrl ? 'Click to view full image' : undefined}
            >
              {candidate.backgroundImageUrl && (
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/50 rounded-full p-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </div>
                </div>
              )}
            </div>

            {/* Gurukul Branding - Top Right */}
            <div className="absolute top-6 right-6 md:top-8 md:right-10 text-white text-right z-10 select-none">
              <div className="flex items-center justify-end gap-3 mb-1">
                  {candidate.logoUrl && (
                <div className="w-12 h-12 md:w-14 md:h-14 bg-white rounded-full flex items-center justify-center overflow-hidden shadow-lg ring-2 ring-white/80">
                    <img src={candidate.logoUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                </div>
                  ) }
              </div>
            </div>
          </div>

          {/* Main Header Content */}
          <div className="px-6 md:px-10 pb-10">
            <div className="flex flex-col md:flex-row items-center md:items-end -mt-24 md:-mt-16 gap-6 relative z-20">

              {/* Profile Picture */}
              <div className="shrink-0 relative group">
                <div
                  className="relative h-64 w-52 md:h-96 md:w-72 rounded-3xl bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden shadow-2xl ring-4 ring-white transition-shadow duration-300"
                >
                  {/* Backend returns base64 in photoKeyUrl and/or photoKey */}
                  {(() => {
                    const photoSrc = candidate.photoKeyUrl ?? candidate.photoKey;
                    return photoSrc && !imageError ? (
                      <>
                        <img
                          src={photoSrc}
                          alt={candidate.name}
                          className="absolute inset-0 w-full h-full object-cover scale-105 cursor-pointer"
                          style={{ objectPosition: 'center 20%' }}
                          onError={() => setImageError(true)}
                          onClick={() => setSelectedImage(photoSrc)}
                        />
                        {/* Zoom hint overlay */}
                        <div
                          className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 flex items-end justify-end p-3 pointer-events-none"
                        >
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/50 rounded-full p-2">
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-5h-4m4 0v4m0-4l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary-100 to-primary-200">
                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white flex items-center justify-center shadow-lg ring-2 ring-primary-100">
                          <span className="text-5xl md:text-6xl font-bold text-primary-600">{candidate.name.charAt(0)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Name & Details */}
              <div className="flex-1 text-center md:text-left pt-4 md:pb-6 w-full">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 leading-tight">{candidate.name}</h1>

                <div className="flex flex-wrap items-center justify-center md:justify-start gap-y-2 gap-x-3 text-gray-600 mb-4">
                  {(candidate.townName || candidate.nagarpalikaGaupalika || candidate.district) && (
                    <span className="flex items-center gap-2 bg-gray-100 px-4 py-1.5 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      {[candidate.townName, candidate.nagarpalikaGaupalika, candidate.district].filter(Boolean).join(', ')}
                    </span>
                  )}
                  {candidate.province && (
                    <span className="flex items-center gap-2 bg-gray-100 px-4 py-1.5 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      {candidate.province}
                    </span>
                  )}
                  {candidate.establishedDate && (
                    <span className="flex items-center gap-2 bg-gray-100 px-4 py-1.5 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      {t('gurukul.establishedDate')}: {formatDate(candidate.establishedDate)}
                    </span>
                  )}
                </div>

                {/* Social Links */}
                {candidate.socialLinks && Object.values(candidate.socialLinks).some(link => link) && (
                  <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-3 mt-1 mb-1">
                    {Object
                      .entries(candidate.socialLinks)
                      .sort(([aKey], [bKey]) => {
                        // Ensure website appears first if present
                        if (aKey === 'website') return -1;
                        if (bKey === 'website') return 1;
                        return 0;
                      })
                      .map(([platform, link]) => (
                        link && (
                          <a
                            key={platform}
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center min-w-[2.25rem] min-h-[2.25rem] text-gray-400 hover:text-primary-600 transition-colors rounded-lg hover:bg-gray-50"
                            title={platform}
                          >
                            <span className="sr-only">{platform}</span>
                            <SocialIcon platform={platform} />
                          </a>
                        )
                      ))}
                  </div>
                )}

              </div>

              {/* QR Code & Actions */}
              <div className="flex flex-col gap-3 w-full md:w-auto">
                {/* Mobile Quick Actions (Share/QR Buttons for Mobile) */}
                <div className="flex md:hidden gap-3 justify-center w-full mb-2">
                  <button
                    onClick={handleShare}
                    className="flex-1 bg-white text-gray-700 py-2 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-sm border border-gray-100 active:bg-primary-50"
                  >
                    {copied ? (
                      <><svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Copied!</>
                    ) : (
                      <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg> Share</>
                    )}
                  </button>
                  <button
                    onClick={handleDownloadQR}
                    className="flex-1 bg-white text-primary-700 py-2 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shadow-sm border border-primary-100 active:bg-primary-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    Save QR
                  </button>
                </div>

                {/* Desktop QR Card (Hidden on Mobile) */}
                <div className="hidden md:flex flex-col items-center bg-white p-4 rounded-2xl shadow-lg border border-gray-100 -mt-24 lg:-mt-32 relative z-30 transition-transform hover:-translate-y-0.5">
                  <div ref={qrRef} className="bg-white p-1 rounded-lg">
                    <QRCode
                      value={scanToDonateUrl}
                      size={120}
                      level="M"
                      bgColor="#ffffff"
                      fgColor="#000000"
                    />
                  </div>
                  <p className="text-xs font-bold text-gray-500 mt-1 uppercase tracking-wide">Scan to Donate</p>

                  {/* QR Actions */}
                  <div className="flex gap-1 mt-1 w-full justify-center">
                    <button onClick={handleShare} className="p-2 bg-primary-50 hover:bg-primary-100 rounded-full text-primary-600 transition-colors" title="Share">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                    </button>
                    <button onClick={handleCopyLink} className="p-2 bg-primary-50 hover:bg-primary-100 rounded-full text-primary-600 transition-colors" title="Copy Link">
                      {copied ? (
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                      )}
                    </button>
                    <button onClick={handleDownloadQR} className="p-2 bg-primary-50 hover:bg-primary-100 rounded-full text-primary-600 transition-colors" title="Download QR">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 md:mt-10 max-w-lg mx-auto md:max-w-none md:mx-0">
              <button
                onClick={handleDonateClick}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white text-lg md:text-xl font-bold py-3 px-4 rounded-2xl flex items-center justify-center gap-2 shadow-xl hover:shadow-2xl hover:-translate-y-0.5 transition"
              >
                <svg className="w-6 h-6 md:w-8 md:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
                <span>
                  Donate to{' '}
                  <span className="underline decoration-primary-400 decoration-2 underline-offset-4">
                    {candidate.name}
                  </span>
                </span>
              </button>
            </div>



            {candidate.bio && (
              <div className="mb-4 mt-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                  {t('gurukul.about', 'About Gurukul')}
                </h2>
                <div className="prose max-w-none text-gray-700 leading-relaxed bg-primary-50 p-3 rounded-2xl border border-primary-100/60 shadow-sm">
                  {renderMarkdownText(candidate.bio)}
                </div>
              </div>
            )}

            {/* Core Academic & Service Details */}
            {(candidate.sanchalitTaha?.length ||
              candidate.padhaiSubject?.length ||
              candidate.areaOfInterest ||
              candidate.expertiseArea ||
              candidate.permanentAddress ||
              (Array.isArray(candidate.sakhaKaryalaya) && candidate.sakhaKaryalaya.length)) && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                  {t('gurukul.keyDetails')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <DetailItem
                    label={t('gurukul.form.sanchalit_taha', 'सञ्चालित तह')}
                    value={candidate.sanchalitTaha}
                  />
                  <DetailItem
                    label={t('gurukul.form.padhai_subject', 'पढाइ विषय')}
                    value={candidate.padhaiSubject}
                  />
                  <DetailItem
                    label={t('gurukul.form.mukhya_sewa_xetra', 'मुख्य सेवा क्षेत्र')}
                    value={candidate.areaOfInterest}
                  />
                  <DetailItem
                    label={t('gurukul.form.sewa_xetra', 'सेवा क्षेत्र')}
                    value={candidate.expertiseArea}
                  />
                  <DetailItem
                    label={t('gurukul.form.mukhya_karyalaya', 'मुख्य कार्यालय')}
                    value={candidate.permanentAddress}
                  />
                  <DetailItem
                    label={t('gurukul.form.sakha_karyalaya', 'शाखा कार्यालयहरू')}
                    value={Array.isArray(candidate.sakhaKaryalaya) ? candidate.sakhaKaryalaya : undefined}
                  />
                </div>
              </div>
            )}

            {/* Specialties Section */}
            {candidate.specialties && candidate.specialties.length > 0 && (
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                  {t('gurukul.specialties')}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {candidate.specialties.map((specialty, idx) => (
                    <div key={idx} className="bg-white p-3 rounded-2xl border-0 shadow-none">
                      <h3 className="text-lg font-bold text-primary-700 mb-2">{specialty.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{specialty.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Achievements Section */}
            {candidate.achievements && candidate.achievements.length > 0 && (
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                  {t('gurukul.achievements')}
                </h2>
                <div className="space-y-2">
                  {candidate.achievements.map((achievement, idx) => {
                    // Backend stores images as base64 data URIs directly.
                    // Use imageUrl ?? image as the fallback.
                    const achImgSrc = achievement.imageUrl ?? achievement.image;
                    return (
                      <div key={idx} className="flex flex-col md:flex-row gap-2 bg-white p-3 rounded-2xl overflow-hidden">
                        {achImgSrc && (
                          <div className="md:w-1/3 shrink-0">
                            <img
                              src={achImgSrc}
                              alt={achievement.title}
                              className="w-full h-48 object-cover rounded-xl cursor-pointer hover:opacity-90 transition"
                              onClick={() => setSelectedImage(achImgSrc)}
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">{achievement.title}</h3>
                          <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">{achievement.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Gallery Section */}
            {candidate.galleryImages && candidate.galleryImages.length > 0 && (
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                  {t('gurukul.gallery')}
                </h2>
                {/* Backend stores gallery images as base64 data URIs directly.
                     Use galleryImagesUrl[idx] ?? img as the fallback. */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {(candidate.galleryImagesUrl ?? candidate.galleryImages ?? []).map((img, idx) => {
                    // If galleryImagesUrl exists as array, prefer per-index value, else use img directly
                    const imgSrc = (candidate.galleryImagesUrl && candidate.galleryImagesUrl[idx])
                      ? candidate.galleryImagesUrl[idx]
                      : img;
                    if (!imgSrc) return null;
                    return (
                      <div key={idx} className="aspect-square rounded-2xl overflow-hidden group border border-gray-200 shadow-md ring-2 ring-white">
                        <img
                          src={imgSrc}
                          alt={`${t('gurukul.gallery')} ${idx}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 cursor-pointer"
                          onClick={() => setSelectedImage(imgSrc)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bank details + QR — single compact card */}
            <div className="mb-4">
              <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <span className="w-2 h-10 bg-green-600 rounded-full"></span>
                {t('gurukul.bankDetails')}
              </h2>

              <div className="rounded-xl bg-white overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="min-w-0 p-2 sm:p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">{t('donate.payment.bankDetails')}</h3>
                      <button
                        type="button"
                        onClick={() => {
                          const bd = candidate.bankDetails || {};
                          const lines = [
                            `${t('donate.payment.bankName')}: ${bd.bank_name || ''}`,
                            `${t('donate.payment.accountNumber')}: ${bd.account_number || ''}`,
                            `${t('donate.payment.accountName')}: ${bd.account_name || ''}`,
                            `${t('donate.payment.branch')}: ${bd.branch || ''}`,
                          ].filter((l) => !l.endsWith(': '));
                          copyBankText('all', lines.join('\n'));
                        }}
                        className="text-sm font-semibold text-primary-600 hover:text-primary-800 px-3 py-1.5 rounded-md hover:bg-primary-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        disabled={
                          !candidate.bankDetails?.bank_name &&
                          !candidate.bankDetails?.account_number &&
                          !candidate.bankDetails?.account_name &&
                          !candidate.bankDetails?.branch
                        }
                      >
                        {bankCopiedKey === 'all' ? t('actions.copyAllSuccess') : t('actions.copyAll')}
                      </button>
                    </div>

                    <div className="space-y-0 pt-0.5">
                      {[
                        {
                          id: 'bank_name',
                          label: t('donate.payment.bankName'),
                          value: candidate.bankDetails?.bank_name || t('common.notProvided'),
                          raw: candidate.bankDetails?.bank_name,
                        },
                        {
                          id: 'account_name',
                          label: t('donate.payment.accountName'),
                          value: candidate.bankDetails?.account_name || t('common.notProvided'),
                          raw: candidate.bankDetails?.account_name,
                        },
                        {
                          id: 'account_number',
                          label: t('donate.payment.accountNumber'),
                          value: candidate.bankDetails?.account_number || '—',
                          raw: candidate.bankDetails?.account_number,
                          highlight: true,
                        },
                        {
                          id: 'branch',
                          label: t('donate.payment.branch'),
                          value: candidate.bankDetails?.branch || '—',
                          raw: candidate.bankDetails?.branch,
                        },
                      ].map((row) => (
                        <div
                          key={row.id}
                          className={`flex items-center gap-2 py-1 sm:py-1.5 ${row.highlight ? 'bg-primary-50/70 -mx-2 sm:-mx-3 px-2 sm:px-3' : ''}`}
                        >
                          <span className="w-[36%] max-w-[12rem] shrink-0 text-sm sm:text-base text-gray-600 leading-tight">
                            {row.label}
                          </span>
                          <span
                            className={`flex-1 min-w-0 break-words ${row.highlight ? 'text-lg sm:text-xl font-mono font-bold text-primary-800' : 'text-base sm:text-lg font-semibold text-gray-900'}`}
                          >
                            {row.value}
                          </span>
                          <button
                            type="button"
                            onClick={() => copyBankText(row.id, row.raw)}
                            className={`shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 hover:bg-gray-100/90 disabled:opacity-40 ${row.highlight ? 'text-primary-700 hover:bg-primary-100/80' : ''}`}
                            disabled={!row.raw}
                            aria-label={t('actions.copy')}
                            title={t('actions.copy')}
                          >
                            {bankCopiedKey === row.id ? (
                              <CheckIcon className={`w-3.5 h-3.5 ${row.highlight ? 'text-primary-700' : 'text-green-600'}`} />
                            ) : (
                              <CopyIcon className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="min-w-0 w-full flex flex-col items-center justify-center gap-1 p-2 sm:p-3 bg-gradient-to-b from-primary-50/40 to-gray-50/30 text-center">
                    <h3 className="text-lg font-bold text-gray-900 leading-tight">{t('gurukul.paymentQrTitle')}</h3>
                    {candidate.bankDetails?.qr_code_keyUrl || candidate.bankDetails?.qr_code_key ? (
                      <>
                        <div className="rounded-xl bg-white p-0.5">
                          <img
                            src={candidate.bankDetails.qr_code_keyUrl ?? candidate.bankDetails.qr_code_key}
                            alt={t('gurukul.paymentQrAlt')}
                            className="w-full max-w-[200px] sm:max-w-[220px] aspect-square object-contain"
                          />
                        </div>
                        <p className="text-xs sm:text-sm font-bold text-primary-600 leading-tight -mt-0.5">
                          {t('gurukul.paymentQrScanHint')}
                        </p>
                      </>
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-center text-gray-400 py-2">
                        <svg className="w-10 h-10 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                        </svg>
                        <span className="text-sm font-medium">QR N/A</span>
                      </div>
                    )}
                    <p className="text-sm text-gray-500 px-2 italic leading-snug max-w-md">
                      {t('gurukul.paymentQrFootnote')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Associated Issues & Solutions */}
            {
              candidate.issuesAndSolutions && candidate.issuesAndSolutions.length > 0 && (
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                    Local Issues & Solutions
                  </h2>
                  <div className="space-y-2">
                    {candidate.issuesAndSolutions.map((item, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-2xl border border-gray-100 shadow-sm">
                        <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                          <span className="text-red-500 font-black">Issue:</span> {item.issue}
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          <span className="text-green-600 font-bold">Solution:</span> {item.solution}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            {/* Campaign Schedule */}
            {
              candidate.campaignSchedule && candidate.campaignSchedule.length > 0 && (
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-8 bg-primary-600 rounded-full"></span>
                    Campaign Schedule
                  </h2>
                  <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm bg-white">
                    <table className="min-w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Location</th>
                          <th className="px-3 py-2 text-left text-xs font-bold text-gray-500 uppercase">Activity</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {candidate.campaignSchedule.map((item, idx) => (
                          <tr key={idx} className="hover:bg-primary-50/50 transition-colors">
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-600">{item.date}</td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{item.location || item.place}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{item.activity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )
            }

            {/* Expenditures List */}
            {
              candidate.expenditures && candidate.expenditures.length > 0 && (
                <div className="mb-4">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-2 h-8 bg-green-600 rounded-full"></span>
                    Campaign Expenditures
                  </h2>
                  <div className="grid grid-cols-1 gap-2">
                    {candidate.expenditures.map((exp) => (
                      <div key={exp.id} className="bg-white rounded-2xl p-3 border border-gray-100 shadow-md hover:shadow-lg transition-shadow">
                        <div className="flex flex-col md:flex-row justify-between md:items-start gap-2">
                          <div className="flex-grow">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-bold text-gray-400 uppercase">{new Date(exp.createdAt).toLocaleDateString()}</span>
                            </div>
                            <h4 className="text-xl font-bold text-gray-900 mb-2">{exp.title}</h4>
                            {exp.description && <p className="text-gray-600 text-sm mb-2 leading-relaxed">{exp.description}</p>}
                          </div>
                        </div>

                        {(exp.imagesUrl || exp.images) && (exp.imagesUrl || exp.images).length > 0 && (
                          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide py-1">
                            {(exp.imagesUrl || exp.images).map((img, idx) => (
                              <a key={idx} href={img} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                <img src={img} alt="Expenditure" className="h-32 w-48 object-cover rounded-xl hover:opacity-90 transition border border-gray-200 shadow-sm" />
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            {
              candidate.recentDonors && candidate.recentDonors.length > 0 && (
                <div className="mb-3">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Recent Donors</h2>
                  <div className="space-y-1">
                    {candidate.recentDonors.map((donor, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-primary-50 rounded-lg border border-primary-100/80 shadow-sm">
                        <div>
                          <p className="font-semibold text-gray-900">{donor.name}</p>
                          <p className="text-sm text-gray-600">{new Date(donor.date).toLocaleDateString()}</p>
                        </div>
                        <div className="text-lg font-bold text-primary-600">
                          {formatCurrency(donor.amount, lang)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }

            <button
              onClick={handleDonateClick}
              className="w-full bg-primary-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Donate Now
            </button>
          </div >
        </div >
      </div >

      <PartyDonationModal
        isOpen={showDonateModal}
        onClose={() => setShowDonateModal(false)}
        initialTargetType="CANDIDATE"
        initialCandidateId={candidate?.id}
      />

      {/* Image Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 animate-fadeIn"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-5xl max-h-[90vh] mx-2 animate-scaleUp">
            <img
              src={selectedImage}
              alt="Gallery Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl ring-1 ring-white/10"
            />
            <button
              className="absolute -top-12 right-0 text-white hover:text-primary-400 transition-colors p-2"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedImage(null);
              }}
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GurukulProfile;
